import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ChevronDown, ChevronUp } from "lucide-react";

const COOKIE_KEY = "ta-cookie-consent";

function getCookieConsent() {
  try {
    const saved = localStorage.getItem(COOKIE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

function saveCookieConsent(consent) {
  localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...consent, date: new Date().toISOString() }));
}

const bannerVariants = {
  initial: { y: 100, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  exit: { y: 100, opacity: 0 },
};

const detailsVariants = {
  initial: { height: 0, opacity: 0 },
  animate: { height: "auto", opacity: 1 },
  exit: { height: 0, opacity: 0 },
};

const cookieCategories = [
  { key: "necessary", label: "Cookie Necessari", desc: "Essenziali per il funzionamento del sito. Sempre attivi.", locked: true },
  { key: "analytics", label: "Cookie Analitici", desc: "Ci aiutano a capire come utilizzi il sito per migliorare l'esperienza." },
  { key: "marketing", label: "Cookie Marketing", desc: "Utilizzati per mostrarti contenuti e offerte personalizzate." },
  { key: "thirdParty", label: "Cookie Terze Parti", desc: "Google Maps, WhatsApp e altri servizi esterni." },
];

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    thirdParty: false,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once on mount
  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: event listener setup once
  useEffect(() => {
    const handler = () => {
      setVisible(true);
      setShowDetails(true);
    };
    window.addEventListener("open-cookie-settings", handler);
    return () => window.removeEventListener("open-cookie-settings", handler);
  }, []);

  const acceptAll = useCallback(() => {
    const consent = { necessary: true, analytics: true, marketing: true, thirdParty: true };
    saveCookieConsent(consent);
    setVisible(false);
  }, []);

  const rejectAll = useCallback(() => {
    const consent = { necessary: true, analytics: false, marketing: false, thirdParty: false };
    saveCookieConsent(consent);
    setVisible(false);
  }, []);

  const savePreferences = useCallback(() => {
    saveCookieConsent(preferences);
    setVisible(false);
  }, [preferences]);

  const togglePref = useCallback((key) => {
    if (key === "necessary") return;
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          variants={bannerVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          data-testid="cookie-banner"
          className="fixed bottom-0 left-0 right-0 z-[60] md:bottom-6 md:left-6 md:right-auto md:max-w-lg"
        >
          <div className="bg-[#141414] border border-white/10 shadow-2xl p-6 md:p-8 mx-4 md:mx-0 mb-4 md:mb-0">
            {/* Header */}
            <div className="flex items-start gap-3 mb-4">
              <Cookie className="w-5 h-5 text-[#C9A646] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <h3 className="text-white text-sm font-medium mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Informativa Cookie
                </h3>
                <p className="text-xs text-[#A1A1AA] leading-relaxed">
                  Utilizziamo cookie per migliorare la tua esperienza di navigazione, analizzare il traffico e personalizzare i contenuti.
                  Puoi scegliere quali cookie accettare.
                </p>
              </div>
            </div>

            {/* Details toggle */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              data-testid="cookie-toggle-details"
              className="flex items-center gap-1 text-xs text-[#C9A646] mb-4 hover:underline"
            >
              {showDetails ? "Nascondi" : "Personalizza"} preferenze
              {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>

            {/* Preferences */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  variants={detailsVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden mb-4"
                >
                  <div className="space-y-3 border-t border-white/5 pt-4">
                    {cookieCategories.map((item) => (
                      <div key={item.key} className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-xs text-white font-medium">{item.label}</p>
                          <p className="text-[10px] text-[#A1A1AA] leading-relaxed">{item.desc}</p>
                        </div>
                        <button
                          onClick={() => togglePref(item.key)}
                          data-testid={`cookie-toggle-${item.key}`}
                          disabled={item.locked}
                          className={`relative w-10 h-5 rounded-full transition-colors duration-300 flex-shrink-0 mt-0.5 ${
                            preferences[item.key] ? "bg-[#C9A646]" : "bg-white/20"
                          } ${item.locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <span
                            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-300 ${
                              preferences[item.key] ? "translate-x-5" : "translate-x-0.5"
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={rejectAll}
                data-testid="cookie-reject-btn"
                className="flex-1 border border-white/20 text-white text-xs py-2.5 px-4 hover:border-[#C9A646]/50 transition-all duration-300"
              >
                Rifiuta
              </button>
              {showDetails ? (
                <button
                  onClick={savePreferences}
                  data-testid="cookie-save-btn"
                  className="flex-1 bg-[#C9A646] text-[#0A0A0A] text-xs font-medium py-2.5 px-4 hover:bg-[#E1C16E] transition-all duration-300"
                >
                  Salva Preferenze
                </button>
              ) : (
                <button
                  onClick={acceptAll}
                  data-testid="cookie-accept-btn"
                  className="flex-1 bg-[#C9A646] text-[#0A0A0A] text-xs font-medium py-2.5 px-4 hover:bg-[#E1C16E] transition-all duration-300"
                >
                  Accetta Tutti
                </button>
              )}
            </div>

            {/* Privacy link */}
            <div className="mt-3 text-center">
              <a
                href="https://customer-assets.emergentagent.com/job_luxury-ride-avellino/artifacts/favlrkbr_Privacy%20Policy%20Kuruma%20Rent.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-[#A1A1AA] hover:text-[#C9A646] underline underline-offset-2 transition-colors"
              >
                Leggi la Privacy Policy completa
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
